// The staff CRM inside the Artemis CRM desktop shell (desktop/, a Tauri window onto
// the LIVE CRM). Everything here is a no-op in a browser tab: the same build serves
// both, and a deploy updates the desktop app without a rebuild.
//
// Why it exists: WKWebView has no tab to open into, so it silently swallows
// `target="_blank"` and `window.open(…)` — every outbound link (customer Proposal
// page, installer portal, payment link, maps) and every PDF preview (a `blob:` URL)
// died with no error. One capture-phase handler fixes all call sites centrally:
//   • an http(s) link / window.open → the Mac's default browser (opener plugin)
//   • window.open(blob:…) → the bytes go to the shell's `open_document` command,
//     which writes a temp file and opens it with the Mac's default viewer
// No npm dependency enters the CRM build: both calls are the raw Tauri IPC that
// `@tauri-apps/api` and `@tauri-apps/plugin-opener` wrap (`plugin:opener|open_url`).

type Invoke = (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;

/** Is the CRM running inside the Tauri desktop shell (vs a plain browser tab)? */
export function isTauri(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as Record<string, unknown>;
  return "__TAURI_INTERNALS__" in w || "__TAURI__" in w;
}

function tauriInvoke(): Invoke | null {
  if (typeof window === "undefined") return null;
  const internals = (window as unknown as { __TAURI_INTERNALS__?: { invoke?: Invoke } }).__TAURI_INTERNALS__;
  return typeof internals?.invoke === "function" ? internals.invoke.bind(internals) : null;
}

export type OpenTarget =
  | { kind: "browser"; url: string }
  | { kind: "document"; url: string }
  | { kind: "native" };

/** Where a `window.open` / `_blank` target belongs in the desktop shell. A relative
 *  path (the cash receipt opens `/api/cash-receipts/…`) is resolved against `origin`
 *  so the default browser receives an absolute URL. */
export function classifyOpen(raw: string, origin: string): OpenTarget {
  if (/^https?:/i.test(raw)) return { kind: "browser", url: raw };
  if (raw.startsWith("blob:")) return { kind: "document", url: raw };
  if (raw.startsWith("/") && !raw.startsWith("//")) return { kind: "browser", url: new URL(raw, origin).toString() };
  return { kind: "native" };
}

const EXTENSION: Record<string, string> = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "text/plain": "txt",
  "text/csv": "csv",
};

/** The temp file name for a document the shell opens — typed by its content, stamped
 *  with the Sydney wall time it was opened (the business clock, AGENTS.md). */
export function documentFileName(contentType: string, now: Date): string {
  const ext = EXTENSION[contentType.split(";")[0]!.trim().toLowerCase()] ?? "bin";
  const parts = new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Sydney",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(now);
  const p = (type: string) => parts.find((x) => x.type === type)?.value ?? "";
  // en-AU formats hour 24 as "24" at midnight in some engines; keep it two digits anyway.
  const hour = p("hour") === "24" ? "00" : p("hour");
  return `artemis-crm-${p("year")}${p("month")}${p("day")}-${hour}${p("minute")}${p("second")}.${ext}`;
}

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  return btoa(binary);
}

/**
 * Route outbound links and document previews to the Mac. Call once at start-up;
 * returns without doing anything outside the desktop shell.
 */
export function installExternalLinkBridge(): void {
  const invoke = tauriInvoke();
  if (!isTauri() || !invoke || typeof document === "undefined") return;

  const openInBrowser = (url: string): void => {
    void invoke("plugin:opener|open_url", { url }).catch((e: unknown) => {
      console.error("[desktop] could not open in the browser 无法在浏览器中打开:", url, e);
    });
  };
  const openDocument = async (blobUrl: string): Promise<void> => {
    try {
      const blob = await (await fetch(blobUrl)).blob();
      const name = documentFileName(blob.type, new Date());
      await invoke("open_document", { name, base64: await blobToBase64(blob) });
    } catch (e) {
      console.error("[desktop] could not open the document 无法打开文件:", e);
    }
  };

  document.addEventListener(
    "click",
    (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a[target="_blank"]') as HTMLAnchorElement | null;
      if (!a?.href) return;
      const target = classifyOpen(a.href, window.location.origin);
      // Jacky, 13 October 2026 (J-0926-10222, from the Bug / Improve button): "the preview function doesnt work".
      // The completion report's "Preview document" is an <a target="_blank"> on a blob: URL; only window.open(blob:)
      // reached open_document, so in the shell the click fell through to a new-window navigation WKWebView cannot
      // make, and nothing happened. A document link takes the same path as window.open(blob:).
      if (target.kind === "document") { e.preventDefault(); void openDocument(target.url); return; }
      if (target.kind !== "browser") return;
      e.preventDefault();
      openInBrowser(target.url);
    },
    true,
  );

  const nativeOpen = window.open.bind(window);
  window.open = ((url?: string | URL, target?: string, features?: string) => {
    const t = classifyOpen(String(url ?? ""), window.location.origin);
    if (t.kind === "browser") { openInBrowser(t.url); return null; }
    if (t.kind === "document") { void openDocument(t.url); return null; }
    return nativeOpen(url as string, target, features);
  }) as typeof window.open;
}
